import { http } from '@/lib/http';

/**
 * Response of `POST /businesses/{business_id}/calendar/feed` — mirrors
 * `controller.calendarFeedResponse`. `url` is the full `.ics` subscription link
 * (composed server-side, `.ics` suffix included); `token` is the raw capability
 * on its own. Returned **once** — a fresh `POST` rotates the token and the old
 * URL immediately 404s. Never persisted client-side.
 */
export interface CalendarFeedDto {
  token: string;
  url: string;
}

/**
 * Response of `GET /businesses/{business_id}/calendar/feed` — status only, no
 * token. `active` is whether the caller currently has a feed; `created_at` is
 * RFC 3339 UTC (or null when inactive).
 */
export interface CalendarFeedStatusDto {
  active: boolean;
  created_at: string | null;
}

export function calendarFeedQueryKey(businessId: string) {
  return ['calendar-feed', businessId] as const;
}

/**
 * Calls `GET /businesses/{business_id}/calendar/feed`. Caller must be an owner
 * or employee of the business (404 otherwise).
 */
export async function getCalendarFeedStatus(
  businessId: string,
): Promise<CalendarFeedStatusDto> {
  const { data } = await http.get<CalendarFeedStatusDto>(
    `/businesses/${businessId}/calendar/feed`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/calendar/feed` (empty body). Mints a
 * fresh feed URL, rotating (and invalidating) any existing one. The token is
 * shown only once. Caller must be an owner or employee.
 */
export async function mintCalendarFeed(
  businessId: string,
): Promise<CalendarFeedDto> {
  const { data } = await http.post<CalendarFeedDto>(
    `/businesses/${businessId}/calendar/feed`,
  );
  return data;
}

/**
 * Calls `DELETE /businesses/{business_id}/calendar/feed`. Revokes the caller's
 * current feed — subscribed calendars go stale. Caller must be an owner or
 * employee.
 */
export async function revokeCalendarFeed(businessId: string): Promise<void> {
  await http.delete(`/businesses/${businessId}/calendar/feed`);
}
